import { ContactViewModel } from "../ViewModels";
import { useContactEnricher } from ".";
import {
    ActionResponse,
    ActionStatusHelper,
    AppEnum,
    AppEnumHelper,
    DomainDetailHook,
    OptionHelper,
    useAppEnum,
    useDetailLifeCycle,
    useExportAction,
} from "@/Shared";
import { DOMAIN_CONFIGS, DomainHookProps } from "@/Shared/Config/domain-config";
import { useContact, useDocument } from "@/OneLiners";
import { ContactModel } from "../ContactModel";


/**
 * useContactDetail hook
 *
 * Domain-specific detail hook for Contact entities.
 * Acts as the orchestration layer between:
 * - domain data access and enrichment,
 * - UI feedback mechanisms (toast notifications, confirmation dialogs),
 * - and navigation concerns.
 *
 * Responsibilities:
 * - Initialize the ContactViewModel when data is available.
 * - Handle delete operations with access control, confirmation modal, and toast feedback.
 * - Manage navigation (form, detail view, layout) based on user actions and permissions.
 * - Expose entity VM, delete handler, access rights, and navigation methods to the UI layer.
 *
 * Author: Samano CASTRE
 * Date: March 2, 2026
 *
 * @see DomainHookProps
 * @see DomainDetailHook<ContactViewModel>
 *
 * @param props DomainHookProps
 * @returns DomainDetailHook<ContactViewModel>
 */
export function useContactDetail(props: DomainHookProps): DomainDetailHook<ContactViewModel> {
    const { permissionHook, accessRight } = props;
    const { domainType, domainLabel } = DOMAIN_CONFIGS.Contact;

    /**
     * Contact domain CRUD operations and state
     */
    const contactHook = useContact();
    const {DocumentEnums} = useAppEnum();

    /**
     * Contact-related enums exposed to the UI layer
     */
    const { ContactEnums, NeedEnums, MatchingEnums } = useAppEnum();

    /**
     * Document data state
     */
    const { dataMap: documentMap, stateResponse: documentResponse } = useDocument();

    /**
     * Contact domain data and operations
     * Provided by the contact enricher facade
     */
    const {
        enricherResponse,
        getStrictEnrichedContact,
        findByDocumentResource,
    } = useContactEnricher();

    /**
     * Combined response from domain and enrichment layers
     */
    const response: ActionResponse = {
        status: ActionStatusHelper.combine(
            contactHook.stateResponse.status,
            enricherResponse.status,
            permissionHook.stateResponse.status
        ),
        error: contactHook.stateResponse.error ??
            enricherResponse.error
    };

    const { isSucceeded: generateIsSucceed } = ActionStatusHelper.is(contactHook.generateAction.response.status);

     /**
     * Detail life cycle hook
     *
     * Handles:
     * - Entity resolution (queryId / entityId)
     * - ViewModel construction
     * - Post-fetch or post-update handling
     * - Provides public API to the UI layer
     */
    const lifeCycleHook = useDetailLifeCycle({
        response,
        domainType,
        props,
        entityHook: contactHook,
        buildVM: (currentEntity: ContactModel, isDisplaying: boolean) => {
            const filteredDocumentMap = findByDocumentResource(documentMap, domainType, currentEntity.id);
            return new ContactViewModel({
                contact: getStrictEnrichedContact(currentEntity),
                viewEnums: ContactEnums,
                needEnums: NeedEnums,
                matchingEnums: MatchingEnums,
                accessRight,
                isDisplaying,
                documentMap: filteredDocumentMap,
            })
        },
        deps: [generateIsSucceed]
    });

    const {entityVM, requestId} = lifeCycleHook;

    
    const {computeUIActions} = useExportAction<ContactModel, ContactViewModel>({
        accessRight : props.accessRight,
        uiFeedbackHooks: props.uiFeedbackHooks,
        entityVM : entityVM,
        domainType,
        domainLabel,
        entityHook: contactHook
    });

    /**
     * Public API exposed to the UI layer
     */
    return {
        ...lifeCycleHook,
        computeUIActions
    }
}